import type { DatasetOutput } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';

/**
 * Every dataset kind as it arrives on the wire, taken from the typed client
 * rather than restated — a second hand-written union is one more place a new
 * vendor kind has to be remembered, and the one the compiler cannot tie back to
 * what the server actually sends.
 */
export type DatasetWireType = DatasetOutput['type'];

/** The wire values for the warehouse-backed dataset kinds. */
export type WarehouseDatasetWireType = Exclude<
  DatasetWireType,
  'csv' | 'manual'
>;

/**
 * Whether a dataset is warehouse-backed, whichever vendor. `'warehouse'` is the
 * legacy Snowflake value kept for rows written before the per-vendor split;
 * `'snowflake'`/`'postgres'` are the vendor kinds. UI that treats any warehouse
 * dataset alike (its query-source actions, the repopulate control) asks this
 * rather than listing the values at every site.
 *
 * An exhaustive switch, not a chain of `===`: a vendor left out of a chain
 * returns false and its dataset silently loses Detect changes, Edit source and
 * repopulate-from-source, with nothing to notice. Left out here, the build
 * breaks.
 */
export const isWarehouseDataset = (
  type: DatasetWireType
): type is WarehouseDatasetWireType => {
  switch (type) {
    case 'csv':
    case 'manual':
      return false;
    case 'warehouse':
    case 'snowflake':
    case 'postgres':
    case 'bigquery':
      return true;
    default:
      return Assert.unreachable(type);
  }
};

/**
 * What the dataset came from, for the one-line subtitle. Manual datasets have
 * no upload behind them, so naming the kind is more useful than a filename
 * placeholder.
 */
export const datasetSourceLabel = (dataset: {
  type: DatasetWireType;
  originalFilename: string | null;
}): string => {
  if (dataset.originalFilename !== null) {
    return dataset.originalFilename;
  }
  switch (dataset.type) {
    case 'manual':
      return 'Manual dataset';
    // Legacy Snowflake rows persisted before the split still read 'warehouse'.
    case 'warehouse':
    case 'snowflake':
      return 'Snowflake dataset';
    case 'postgres':
      return 'Postgres dataset';
    case 'bigquery':
      return 'BigQuery dataset';
    case 'csv':
      return 'CSV dataset';
  }
};

export const formatSize = (
  sizeBytes: number | null,
  unknownLabel = 'Unknown'
): string => {
  if (sizeBytes === null) {
    return unknownLabel;
  }
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KB`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * One entry in the dataset menu's "where the rows come from" group.
 *
 * Named rather than rendered here so the composition can be checked without a
 * page: which entries a kind offers is the part that goes wrong when a kind is
 * added, and it decides whether the group is shown flat or folded away.
 */
export type DatasetSourceMenuEntry =
  | 'edit-source'
  | 'detect-changes'
  | 'change-detection-schedule'
  | 'flush-to-warehouse'
  | 'write-back-table'
  | 'reupload-csv'
  | 'set-row-keys';

/**
 * The source entries a kind offers, in the order they are shown.
 *
 * Ordered by what a reader reaches for: change where the rows come from, then
 * check it, then the settings underneath it. The schedule sits next to the
 * manual scan because it is the same scan, run without being asked.
 * `set-row-keys` is last and on every kind — every dataset has row identity,
 * whatever fills it.
 */
export const datasetSourceMenuEntries = (
  type: DatasetWireType
): DatasetSourceMenuEntry[] => [
  ...(isWarehouseDataset(type)
    ? ([
        'edit-source',
        'detect-changes',
        'change-detection-schedule',
        'flush-to-warehouse',
        'write-back-table',
      ] as const)
    : []),
  ...(type === 'csv' ? (['reupload-csv'] as const) : []),
  'set-row-keys',
];

/**
 * Whether the source entries are worth folding into a submenu.
 *
 * A warehouse dataset brings five and would otherwise push placement and
 * archiving off the bottom of a menu nobody can scan; a manual dataset brings
 * one, which a submenu would only bury. The threshold is where hiding entries
 * starts to buy more than it costs.
 */
export const groupsDatasetSourceMenu = (
  entries: readonly DatasetSourceMenuEntry[]
): boolean => entries.length > 2;
